import type { PlanningItem } from './core';

export type PlanningSeoTextItem = Pick<
  PlanningItem,
  'description' | 'seoContext' | 'seoSummary' | 'seoBullets'
>;

const DEFAULT_MAX_LENGTH = 760;
const DUPLICATE_TOKEN_MIN_LENGTH = 34;

const normalizePlanningText = (value: unknown) =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const normalizeComparablePlanningText = (value: unknown) =>
  normalizePlanningText(value)
    .replace(/[.,:;!?()[\]"'׳״־–—-]/g, '')
    .toLocaleLowerCase();

const trimPlanningText = (value: string, maxLength: number) => {
  const text = normalizePlanningText(value);
  if (text.length <= maxLength) return text;

  const shortened = text.slice(0, maxLength + 1);
  const lastSpaceIndex = shortened.lastIndexOf(' ');
  const trimIndex = lastSpaceIndex > Math.min(180, Math.floor(maxLength * 0.55))
    ? lastSpaceIndex
    : maxLength;

  return `${shortened.slice(0, trimIndex).trim()}...`;
};

const isDistinctPlanningPart = (candidate: string, selected: string[]) => {
  const comparableCandidate = normalizeComparablePlanningText(candidate);
  if (!comparableCandidate) return false;

  return selected.every((existing) => {
    const comparableExisting = normalizeComparablePlanningText(existing);
    if (comparableCandidate === comparableExisting) return false;

    const shortLength = Math.min(
      comparableCandidate.length,
      comparableExisting.length,
    );

    if (shortLength < DUPLICATE_TOKEN_MIN_LENGTH) return true;

    return !(
      comparableCandidate.includes(comparableExisting) ||
      comparableExisting.includes(comparableCandidate)
    );
  });
};

export function buildPlanningItemSeoText(
  item: PlanningSeoTextItem,
  options: {
    maxLength?: number;
    seoBulletsLimit?: number;
    allowPartialLastPart?: boolean;
  } = {}
) {
  const maxLength = options.maxLength ?? DEFAULT_MAX_LENGTH;
  const allowPartialLastPart = options.allowPartialLastPart ?? true;
  const seoBullets =
    typeof options.seoBulletsLimit === 'number'
      ? (item.seoBullets ?? []).slice(0, options.seoBulletsLimit)
      : item.seoBullets ?? [];
  const candidates = [
    item.description,
    item.seoContext,
    item.seoSummary,
    ...seoBullets,
  ].map(normalizePlanningText);
  const selected: string[] = [];

  for (const candidate of candidates) {
    if (!candidate || !isDistinctPlanningPart(candidate, selected)) continue;

    const currentText = selected.join(' ');
    const nextText = normalizePlanningText([currentText, candidate].filter(Boolean).join(' '));

    if (nextText.length > maxLength) {
      if (!allowPartialLastPart) break;

      const remaining = maxLength - currentText.length - (currentText ? 1 : 0);

      if (remaining > 140) {
        selected.push(trimPlanningText(candidate, remaining));
      } else if (!selected.length) {
        selected.push(trimPlanningText(candidate, maxLength));
      }

      break;
    }

    selected.push(candidate);
  }

  return trimPlanningText(selected.join(' '), maxLength);
}
